import React, { useState, useEffect } from 'react';
import { View, FlatList, StyleSheet, Pressable } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigation } from '@react-navigation/native';

import { selectExpenseStates } from '../../store/selectors/expense.selector';
import { scale, scaleVertically } from '../../utils/scale.utils';
import { FileButton } from '../FileButton';
import { Color } from '../../styles';
import { setLostReceipt, setReceiptsState } from '../../store/actions/expense';
import AddReceiptDropDown from './AddReceiptDropDown';
import {
  PHOTO_LIBRARY_OPTION,
  TAKE_PHOTO_OPTION,
  LOST_RECEIPT_OPTION,
  PHOTO_UPLOADS,
} from '../../constants/Enums';
import { PDF_MIME_TYPE, pickImage } from '../../utils/image.utils';
import { Modals } from '../../constants/ScreenNames';
import Camera from '../Camera';
import { isPdf } from '../../utils/common.utils';

const ADD_RECEIPT_ID = 'ADD';
const MAX_RECEIPTS_AMOUNT = 5;
const receiptsComponentHeight = scaleVertically(50);

const ReceiptsBar = ({ isEditable, actionType, subTab, updateExpenseState = true }) => {
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const expenseStates = useSelector(selectExpenseStates)[actionType][subTab];
  const receipts = expenseStates?.receipts;
  const [showAddReceiptOptions, setShowOptions] = useState(false);
  const [optionsArrayState, setOptionsArrayState] = useState([]);
  const [enableCamera, setEnableCamera] = useState(false);

  const toggleOptions = () => setShowOptions((prev) => !prev);

  const removeReceipt = (id) => {
    dispatch(
      setReceiptsState(
        receipts.filter((item) => item.id !== id),
        actionType,
        subTab
      )
    );
  };

  const onSelectImage = (result) => {
    navigation.navigate(Modals.ReceiptScannerModal, {
      file: result?.assets?.[0]?.base64,
      actionType,
      subTab,
      updateExpenseState,
      isPDF: result?.assets?.[0]?.mimeType === PDF_MIME_TYPE,
    });
  };

  const renderItem = ({ item }) => {
    if (item.id === ADD_RECEIPT_ID) {
      return (
        <View>
          <FileButton
            fileName="Add Receipt"
            setWidth={false}
            isAddReceipt={true}
            handlePress={toggleOptions}
            isEditable={isEditable}
          />
        </View>
      );
    }

    if (item.id === LOST_RECEIPT_OPTION.id) {
      return (
        <View>
          <FileButton
            fileName="No Receipt"
            removeReceipt={() => removeReceipt(LOST_RECEIPT_OPTION.id)}
            setWidth={false}
            // textColor={Color.black}
            isEditable={isEditable}
          />
        </View>
      );
    }

    return (
      <View>
        <FileButton
          fileName={`Receipt${item.id}${isPdf(item?.s3FileName) ? '.pdf' : '.jpg'}`}
          removeReceipt={() => removeReceipt(item.id)}
          setWidth={false}
          handlePress={() => {
            navigation.navigate(Modals.ViewReceiptModal, {
              receipt: item,
              showSelectReceipt: false,
              removeSelectedImageFromState: () => {
                removeReceipt(item.id);
              },
              isEditable,
            });
          }}
          isEditable={isEditable}
        />
      </View>
    );
  };

  useEffect(() => {
    const COMMON_RECEIPT_OPTIONS = [
      {
        ...PHOTO_LIBRARY_OPTION,
        onPress: () => {
          pickImage(onSelectImage, true);
          toggleOptions();
        },
      },
      {
        ...TAKE_PHOTO_OPTION,
        onPress: () => {
          setEnableCamera(true);
          toggleOptions();
        },
      },
      {
        ...PHOTO_UPLOADS,
        onPress: () => {
          navigation.navigate(Modals.ReceiptVaultModal, { actionType, subTab, updateExpenseState });
          toggleOptions();
        },
      },
    ];

    if (receipts?.length > 0) {
      setOptionsArrayState(COMMON_RECEIPT_OPTIONS);
    } else {
      setOptionsArrayState([
        ...COMMON_RECEIPT_OPTIONS,
        {
          ...LOST_RECEIPT_OPTION,
          onPress: () => {
            dispatch(setLostReceipt(actionType, subTab));
            toggleOptions();
          },
        },
      ]);
    }
  }, [receipts]);

  return (
    <>
      {enableCamera ? (
        <Camera
          onClose={() => setEnableCamera(false)}
          showRotate
          showGallery
          onGetImage={(data) => {
            setEnableCamera(false);
            navigation.navigate(Modals.ReceiptScannerModal, {
              file: data.base64,
              actionType,
              subTab,
              updateExpenseState,
              isPDF: data.mimeType === PDF_MIME_TYPE,
            });
          }}
          isExpense={true}
        />
      ) : null}
      {isEditable || receipts?.length > 0 ? (
        <Pressable style={styles.container} onPress={() => setShowOptions(false)}>
          <FlatList
            data={
              isEditable && receipts?.length < MAX_RECEIPTS_AMOUNT
                ? [{ id: ADD_RECEIPT_ID }, ...receipts]
                : receipts
            }
            renderItem={renderItem}
            keyExtractor={(item) => item.id}
            horizontal
            contentContainerStyle={styles.contentContainer}
          />
        </Pressable>
      ) : (
        <View style={styles.container}>
          <FlatList
            data={[LOST_RECEIPT_OPTION]}
            renderItem={renderItem}
            keyExtractor={(item) => item.id}
            horizontal
            contentContainerStyle={styles.contentContainer}
          />
        </View>
      )}
      {showAddReceiptOptions && (
        <AddReceiptDropDown
          receiptsComponentHeight={receiptsComponentHeight}
          optionsArrayState={optionsArrayState}
          onClose={() => setShowOptions(false)}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    height: receiptsComponentHeight,
    marginTop: scaleVertically(14),
  },
  contentContainer: {
    paddingHorizontal: scale(6),
    alignSelf: 'center',
  },
});

export default ReceiptsBar;
